import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useApi } from '../useApi';
import { showPill } from './pillBus';
import { applyPendingSociety, initPush } from './push';
import { useNotificationTap } from './useNotificationTap';

/**
 * Push notifications, wired in one place — the same shape as `SyncProcessor`
 * in App.js, and for the same reason: receiving a notification belongs at the
 * top of the app, not on whichever screen happens to be open when one arrives.
 *
 * THREE JOBS. It reconciles this device's subscription on every start (browsers
 * drop them silently, so "we registered once" is not a durable fact); it turns
 * a TAP into "switch to that society, then go Home"; and it applies a society
 * that arrived before the profile did — either parked by a cold-start tap, or
 * carried on `?society=` by the service worker opening a fresh window.
 *
 * The query parameter is STRIPPED once applied (`replace: true`), so a reload
 * or a share of the URL doesn't silently re-switch society later.
 *
 * **AN EFFECT HERE RETURNS A CLEANUP FUNCTION OR NOTHING AT ALL.** React calls
 * whatever an effect returns when it tears the effect down, so returning the
 * society that was just applied — an object, or null — crashed the app on
 * load with "destroy is not a function". StrictMode double-invokes effects in
 * development, which is what made it fire immediately rather than on some
 * later unmount.
 */
const PushBridge = () => {
  const { authenticatedFetch } = useApi();
  const { currentUser, userProfile, setCurrentSociety } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Shared with the in-app pill, so a tap does the same thing whichever
  // surface it lands on.
  const onTap = useNotificationTap();

  useEffect(() => {
    if (!currentUser) return undefined;
    // initPush returns its teardown — the one thing that SHOULD be returned.
    return initPush({
      api: authenticatedFetch,
      // Absent on a profile cached before notifications shipped — the column
      // defaults on, so treat it that way.
      pushEnabled: userProfile?.push_enabled !== false,
      onTap,
      // A push arriving while the app is IN FRONT: the service worker has
      // suppressed the system banner, so this is the only thing that will
      // show it. Not on an iOS PWA, where the banner is shown instead.
      showPill,
    });
  }, [authenticatedFetch, currentUser, userProfile?.push_enabled, onTap]);

  useEffect(() => {
    if (!userProfile) return;
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('society');
    applyPendingSociety(userProfile, setCurrentSociety, fromUrl);
    if (fromUrl) {
      params.delete('society');
      const qs = params.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' },
               { replace: true });
    }
    // No return value: see the note above.
  }, [userProfile, location.search, location.pathname, navigate, setCurrentSociety]);

  return null;
};

export default PushBridge;
